import {
	Button,
	MenuItem,
	SelectTabData,
	SelectTabEvent,
	Tab,
	TabList,
	Text,
} from "@fluentui/react-components";
import { useCallback } from "react";
import { TabItemData, TabListViewProps } from "./types";
import { useStyle } from "./style";
import { addBreadcrumb } from "@/common/helpers/sentryHelper";
import { useResponsiveBreakpoints } from "@/hooks/responsive";
import { NavigationFilled } from "@fluentui/react-icons";
import { MenuListPopover } from "@/components/atoms/menuList";
import {
	NavigationActions,
	useNavigationStore,
} from "@/common/stores/persistingStores/navigation";

export const TabListView = (props: TabListViewProps) => {
	const { selected, onTabSelect, tabs } = props;
	const classes = useStyle();

	const setToggledTab = useNavigationStore(
		(state: NavigationActions) => state.setToggledTab
	);

	const onTabSelectCallback = useCallback(
		(_: SelectTabEvent, data: SelectTabData) => {
			addBreadcrumb("ui_interaction", `Selected tab: ${data?.value}`);
			onTabSelect(data.value);
		},
		[onTabSelect]
	);

	const setIsMobileDetailViewMode = useNavigationStore(
		(actions: NavigationActions) => actions.setIsMobileDetailViewMode
	);

	const menuOrTabItemContent = useCallback(
		(data: TabItemData) => (
			<>
				<Text
					title={data.title}
					className={
						data.key === selected ? classes.selectedTitle : classes.title
					}
				>
					{data.title}
				</Text>
				{!!data.subTitle && (
					<Text
						weight="bold"
						className={
							data.key === selected ? classes.selectedTitle : classes.title
						}
					>
						{" "}
						({data.subTitle})
					</Text>
				)}
			</>
		),
		[selected]
	);

	const buildTabItem = useCallback(
		(data: TabItemData) => (
			<Tab key={data.key} value={data.key}>
				{menuOrTabItemContent(data)}
			</Tab>
		),
		[selected, menuOrTabItemContent]
	);

	const buildMenuItem = useCallback(
		(data: TabItemData) => (
			<MenuItem
				key={data.key}
				title={data.title}
				onClick={() => {
					addBreadcrumb("ui_interaction", `Selected tab: ${data?.key}`);
					setToggledTab(undefined);
					setIsMobileDetailViewMode(false);
					onTabSelect(data.key);
				}}
				children={menuOrTabItemContent(data)}
			/>
		),
		[selected, menuOrTabItemContent]
	);

	const { isMobile } = useResponsiveBreakpoints();

	const desktopView = useCallback(
		() => (
			<TabList selectedValue={selected} onTabSelect={onTabSelectCallback}>
				{tabs.map(buildTabItem)}
			</TabList>
		),
		[selected, tabs]
	);

	const mobileView = useCallback(
		() => (
			<MenuListPopover
				closeOnScroll
				trigger={
					<Button
						shape="circular"
						appearance="transparent"
						icon={<NavigationFilled fontSize={20} />}
						className={classes.burgerMenuButton}
					/>
				}
				items={tabs.map(buildMenuItem)}
			/>
		),
		[selected, tabs]
	);

	return isMobile ? mobileView() : desktopView();
};
